'use client';
import { Box, Typography, useTheme } from '@mui/material';

import { Avatar } from '@/components';
import { isDarkColor } from '@/utils';

interface TeamInfoProps {
  /** The abbreviation of the team (e.g., 'LAL', 'BOS') */
  abbr: string;
  /** The full name of the team */
  name: string;
  /** Optional team record (e.g., '12-4') */
  record?: string | number;
  /** The primary brand color for the team */
  color?: string;
  /** Real-time score for live events, if applicable */
  liveScore?: string;
  /** Optional team logo URL */
  logo?: string;
  /** Whether this team is the winner of the game */
  isWinner?: boolean;
  /** Whether the game is currently live */
  isLive?: boolean;
  isPreviousMarket?: boolean;
  hideLiveScore?: boolean;
}

/**
 * TeamInfo displays a single team's basic identity within a game card.
 */
const TeamInfo = ({
  abbr,
  name,
  record,
  color,
  liveScore,
  logo,
  isWinner,
  isLive,
  isPreviousMarket,
  hideLiveScore,
}: TeamInfoProps) => {
  const theme = useTheme();
  const badgeColor = color || theme.palette.primary.main;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
      {liveScore && !hideLiveScore && (
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isPreviousMarket && !isWinner ? '' : isPreviousMarket ? 'white' : 'black',
            fontSize: '0.65rem',
            fontWeight: 800,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            flexShrink: 0,
            bgcolor: isPreviousMarket && !isWinner ? '' : isPreviousMarket ? 'black' : 'white',
          }}
        >
          {liveScore}
        </Box>
      )}

      <Avatar
        src={logo}
        alt={name || abbr}
        size={32}
        fallbackLabel={abbr}
        sx={{
          borderRadius: '4px',
          bgcolor: logo ? 'transparent' : badgeColor,
          color: isDarkColor(badgeColor) ? '#ffffff' : '#000000',
          '& .MuiTypography-root': {
            fontSize: '0.65rem',
            fontWeight: 800,
            color: 'inherit',
          },
          ...(isPreviousMarket &&
            !isWinner && {
              opacity: 0.5,
            }),
        }}
      />

      <Typography
        sx={{
          fontSize: '0.9375rem',
          fontWeight: 700,
          color: 'text.primary',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          width: 160,
          textOverflow: 'ellipsis',
          flexShrink: 1,
          minWidth: 0,
          ...(isPreviousMarket &&
            !isWinner && {
              opacity: 0.5,
            }),
        }}
      >
        {name || ''}
      </Typography>
      {!hideLiveScore &&
        (isLive ||
          (typeof record === 'number' ? record > 0 : parseInt(record as string, 10) > 0)) &&
        record !== null &&
        record !== undefined &&
        record !== '' && (
          <Typography
            sx={{
              fontSize: '0.8125rem',
              color: 'text.secondary',
              opacity: 0.6,
              whiteSpace: 'nowrap',
              flexShrink: 0,
            }}
          >
            {record}
          </Typography>
        )}
    </Box>
  );
};

export default TeamInfo;
